import { debounce } from 'lodash-es';
import {
  type ComponentPropsWithRef,
  createElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { fromEvent, tap } from 'rxjs';

import { useMergedRef, useSubscriptions } from '@/hook';

import { VFlex } from '../VFlex';
import { new__cn as cn } from '../util';
import style from './style';
import type { VVirtualScrollProps } from './type';

const RANGE_SIZE = 20;

const VVirtualScroll = <T,>({
  item: { gap = 0, height, list },
  renderItem,
  ItemListWrapper,
  className,
  ref,
  ...rest
}: VVirtualScrollProps<T> & ComponentPropsWithRef<'div'>) => {
  const [itemRange, setItemRange] = useState([0, 1] as [number, number]);

  const $scrollContainer = useRef<HTMLDivElement>(null);
  const mergedRef = useMergedRef($scrollContainer as any, ref as any);

  const length = useMemo(() => list.length, [list]);
  const scrollHeight = useMemo(
    () => length * height + length * gap,
    [length, height, gap],
  );

  const changeItemRange = debounce(() => {
    if (!$scrollContainer.current) return;

    const { scrollTop, offsetHeight } = $scrollContainer.current;

    const startIndex = Math.max(
      Math.floor(scrollTop / (height + gap)) - RANGE_SIZE,
      0,
    );
    const endIndex =
      startIndex +
      Math.min(
        Math.ceil(offsetHeight / (height + gap)) + RANGE_SIZE * 2,
        length - 1,
      );

    setItemRange([startIndex, endIndex + 1]);
  }, 10);

  useSubscriptions(
    () => [
      fromEvent(window, 'resize').pipe(tap(changeItemRange)).subscribe(),

      fromEvent<UIEvent>($scrollContainer.current!, 'scroll')
        .pipe(tap(changeItemRange))
        .subscribe(),
    ],
    [scrollHeight, itemRange, length, gap, height],
  );

  useEffect(() => {
    if (!$scrollContainer.current) return;

    const renderedItemList = [
      ...$scrollContainer.current.querySelectorAll<HTMLDivElement>(
        '[aria-rowindex]',
      ),
    ];
    const maxItemWidth = renderedItemList.reduce(
      (max, cur) => (max <= cur.offsetWidth ? cur.offsetWidth : max),
      0,
    );

    const $firstChild = $scrollContainer.current.firstChild as HTMLElement;

    if ($firstChild) {
      const newMinWidth = `${maxItemWidth - 10}px`;

      if ($firstChild.style.minWidth !== newMinWidth) {
        $firstChild.style.minWidth = newMinWidth;
      }
    }
  }, [itemRange]);

  useEffect(() => {
    changeItemRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [length, gap, height]);

  useEffect(
    () => {
      if (!$scrollContainer.current) return;

      changeItemRange();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <VFlex
      {...rest}
      ref={mergedRef}
      className={cn(className, style.vVirtualScrollContainer)}
      data-testid="v-virtual-scroll-container"
      role="table"
    >
      {createElement(
        ItemListWrapper ?? 'div',
        {
          className: style.vVirtualScrollWrapper,
          style: { height: `${scrollHeight - 2}px` },
          'data-testid': 'v-virtual-scroll-wrapper',
        } as any,
        <div
          data-testid="v-virtual-scroll-item-list"
          className="relative"
          role="rowgroup"
        >
          {list.slice(...itemRange).map((item, i) => (
            <div
              key={i + itemRange[0]}
              className={style.vVirtualScrollItem}
              aria-rowindex={i + itemRange[0] + 1}
              role="row"
              style={{
                top: `${(i + itemRange[0]) * height + (i + itemRange[0]) * gap}px`,
              }}
            >
              {renderItem(item, i + itemRange[0])}
            </div>
          ))}
        </div>,
      )}
    </VFlex>
  );
};

export default VVirtualScroll;
